import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  ASSIGNMENT_PRIORITIES,
  DEFAULT_ASSIGNMENT_PRIORITY,
  type AssignmentPriority,
  type CapacityCheckInput,
  type PayableStage,
} from '@tailoring-crm/shared';
import { Button, Input, Label } from '@tailoring-crm/ui';
import { minutesFromHours } from '../../lib/capacity/capacity-request.js';

/**
 * "Can we deliver by the 24th?", as a form.
 *
 * HOURS IN, MINUTES OUT. A shop says "about three hours", never "a hundred and eighty minutes",
 * so that is what the boxes collect — and the contract carries whole minutes and nothing else,
 * so that is what leaves. The conversion happens once, on the way out, and is the only
 * arithmetic on this screen.
 *
 * The task type options are the SHOP'S stages with the SHOP'S labels, handed in from above. No
 * stage key and no stage label is written anywhere in this file: a shop that calls stitching
 * "tailoring" sees "tailoring", and a shop that has switched a stage off never sees it at all.
 *
 * Rows carry a generated id rather than leaning on their position, because removing the middle
 * row of three must not hand the third row the second one's input state.
 */

export interface CapacityStageOption {
  key: PayableStage;
  label: string;
}

export interface CapacityCheckFormProps {
  /** The shop's enabled payable stages, in workflow order, with the shop's own labels. */
  stages: readonly CapacityStageOption[];
  pending: boolean;
  onCheck: (request: CapacityCheckInput) => void;
}

interface TaskRow {
  id: string;
  taskType: PayableStage;
  hours: string;
  priority: AssignmentPriority;
  splittable: boolean;
  overtimeHours: string;
}

let rowSequence = 0;
function newRow(taskType: PayableStage): TaskRow {
  rowSequence += 1;
  return {
    id: `capacity-task-${rowSequence}`,
    taskType,
    hours: '1',
    priority: DEFAULT_ASSIGNMENT_PRIORITY,
    splittable: false,
    overtimeHours: '',
  };
}

export function CapacityCheckForm({
  stages,
  pending,
  onCheck,
}: CapacityCheckFormProps): ReactElement {
  const { t } = useTranslation();
  const firstStage = stages[0] as CapacityStageOption;

  const [deliveryDay, setDeliveryDay] = useState('');
  const [rows, setRows] = useState<TaskRow[]>(() => [newRow(firstStage.key)]);

  const patchRow = (id: string, patch: Partial<TaskRow>): void => {
    setRows((previous) => previous.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const submit = (): void => {
    onCheck({
      deliveryDay,
      tasks: rows.map((row) => ({
        taskType: row.taskType,
        estimatedMinutes: minutesFromHours(row.hours),
        priority: row.priority,
        splittable: row.splittable,
        otMinutes: minutesFromHours(row.overtimeHours),
      })),
    });
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="flex max-w-xs flex-col gap-1.5">
        <Label htmlFor="capacity-delivery-day">{t('capacity.form.deliveryDate')}</Label>
        <Input
          id="capacity-delivery-day"
          type="date"
          required
          value={deliveryDay}
          onChange={(event) => setDeliveryDay(event.target.value)}
        />
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground">{t('capacity.form.tasks')}</h3>
        <Button
          type="button"
          size="sm"
          onClick={() => setRows((previous) => [...previous, newRow(firstStage.key)])}
        >
          <Plus aria-hidden="true" />
          {t('capacity.form.addTask')}
        </Button>
      </div>

      <ul className="flex flex-col gap-3">
        {rows.map((row, index) => (
          <li
            key={row.id}
            data-testid="capacity-task-row"
            className="flex flex-col gap-3 rounded-lg border border-border p-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {t('capacity.form.taskNumber', { number: index + 1 })}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={rows.length === 1}
                aria-label={t('capacity.form.removeTask', { number: index + 1 })}
                onClick={() => setRows((previous) => previous.filter((one) => one.id !== row.id))}
              >
                <Trash2 aria-hidden="true" />
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${row.id}-type`}>{t('capacity.form.taskType')}</Label>
                <select
                  id={`${row.id}-type`}
                  value={row.taskType}
                  onChange={(event) =>
                    patchRow(row.id, { taskType: event.target.value as PayableStage })
                  }
                  className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                >
                  {stages.map((stage) => (
                    <option key={stage.key} value={stage.key}>
                      {stage.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${row.id}-hours`}>{t('capacity.form.hours')}</Label>
                <Input
                  id={`${row.id}-hours`}
                  inputMode="decimal"
                  value={row.hours}
                  onChange={(event) => patchRow(row.id, { hours: event.target.value })}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${row.id}-priority`}>{t('capacity.form.priority')}</Label>
                <select
                  id={`${row.id}-priority`}
                  value={row.priority}
                  onChange={(event) =>
                    patchRow(row.id, { priority: event.target.value as AssignmentPriority })
                  }
                  className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                >
                  {ASSIGNMENT_PRIORITIES.map((one) => (
                    <option key={one} value={one}>
                      {t(`assignments.priorityValue.${one}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${row.id}-overtime`}>{t('capacity.form.overtimeHours')}</Label>
                <Input
                  id={`${row.id}-overtime`}
                  inputMode="decimal"
                  value={row.overtimeHours}
                  onChange={(event) => patchRow(row.id, { overtimeHours: event.target.value })}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="size-4"
                checked={row.splittable}
                onChange={(event) => patchRow(row.id, { splittable: event.target.checked })}
              />
              {t('capacity.form.splittable')}
            </label>
          </li>
        ))}
      </ul>

      <Button type="submit" disabled={deliveryDay === '' || pending}>
        {t('capacity.form.check')}
      </Button>
    </form>
  );
}
